import React from "react";
import { useDashboardSummary } from "../../api/dashboard";
import { formatBRL, formatDate } from "../../utils/money";
import { StatusTag } from "../../components/StatusTag";

// Cores do gráfico recebido x pago — par validado (contraste e daltonismo) no canvas de design.
const COLOR_RECEBIDO = "#16856A";
const COLOR_PAGO = "#C2721F";
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const COMPACT = new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 });

// topo do eixo Y "redondo" (4 faixas de passo 1/2/2,5/5 × 10^n) pra as linhas de grade caírem em
// valores legíveis.
function niceTop(max: number): number {
  if (max <= 0) return 4;
  const raw = max / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / mag;
  const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
  return step * 4;
}

function Fig({ label, value, note, bad }: { label: string; value: string; note: string; bad?: boolean }) {
  return (
    <div className="nt-fig">
      <div className="nt-fig-label">{label}</div>
      <div className={"nt-fig-value" + (bad ? " bad" : "")}>{value}</div>
      <div className="nt-fig-note">{note}</div>
    </div>
  );
}

export default function DashboardPage() {
  const { data, isLoading, isError } = useDashboardSummary();

  if (isLoading) return <div className="text-secondary">Carregando…</div>;
  if (isError || !data) return <div className="text-danger">Não foi possível carregar o painel.</div>;

  const { kpis, cashFlow, revenueByApplication, upcoming, activity } = data;
  const maxRevenue = Math.max(1, ...revenueByApplication.map((r) => r.valor));
  const top = niceTop(Math.max(0, ...cashFlow.flatMap((f) => [f.recebido, f.pago])));
  const pct = (v: number) => `${Math.min(100, (v / top) * 100)}%`;
  const cols = { gridTemplateColumns: `repeat(${Math.max(1, cashFlow.length)}, minmax(0, 1fr))` };

  return (
    <div className="d-flex flex-column gap-3">
      <section className="nt-figs" aria-label="Indicadores">
        <Fig label="Receita recorrente (MRR)" value={formatBRL(kpis.mrr)} note={`${kpis.subscribers} assinante${kpis.subscribers === 1 ? "" : "s"}`} />
        <Fig label="A receber" value={formatBRL(kpis.receivableOpen)} note={`${kpis.receivableOpenCount} título(s) em aberto`} />
        <Fig label="Em atraso" value={formatBRL(kpis.overdue)} note={`${kpis.overdueCount} título(s) vencido(s)`} bad={kpis.overdue > 0} />
        <Fig label="A pagar" value={formatBRL(kpis.payableOpen)} note={`${kpis.payableOpenCount} conta(s) prevista(s)`} />
      </section>

      <div className="nt-grid-2" style={{ gap: 16 }}>
        <div className="d-flex flex-column gap-3 min-w-0">
          <section className="nt-card">
            <div className="nt-panel-head">
              <h2>Fluxo de caixa · {cashFlow.length} meses</h2>
              <div className="nt-legend">
                <span>
                  <i style={{ background: COLOR_RECEBIDO }} />
                  Recebido
                </span>
                <span>
                  <i style={{ background: COLOR_PAGO }} />
                  Pago
                </span>
              </div>
            </div>
            <div className="nt-panel-body">
              <div className="nt-chart" role="img" aria-label="Recebido e pago por mês">
                <div className="nt-chart-y" aria-hidden="true">
                  {[4, 3, 2, 1, 0].map((i) => (
                    <span key={i}>{i === 0 ? "0" : COMPACT.format((top / 4) * i)}</span>
                  ))}
                </div>
                <div className="nt-chart-plot" style={cols}>
                  {cashFlow.map((f) => (
                    <div key={f.mes} className="nt-chart-group">
                      <span
                        className="nt-chart-bar"
                        title={`${f.mes} · recebido ${formatBRL(f.recebido)}`}
                        style={{ height: pct(f.recebido), background: COLOR_RECEBIDO }}
                      />
                      <span className="nt-chart-bar" title={`${f.mes} · pago ${formatBRL(f.pago)}`} style={{ height: pct(f.pago), background: COLOR_PAGO }} />
                    </div>
                  ))}
                </div>
                <div className="nt-chart-x" style={cols} aria-hidden="true">
                  {cashFlow.map((f) => (
                    <span key={f.mes}>{f.mes}</span>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="nt-card">
            <div className="nt-panel-head">
              <h2>Receita por aplicação</h2>
              <span className="nt-meta">contratos vigentes</span>
            </div>
            <div className="nt-panel-body" style={{ paddingTop: 8, paddingBottom: 8 }}>
              {revenueByApplication.length === 0 && (
                <div className="text-secondary py-2" style={{ fontSize: 13 }}>
                  Nenhuma aplicação com contrato vigente ainda.
                </div>
              )}
              {revenueByApplication.map((r) => (
                <div key={r.nome} className="nt-hbar">
                  <span className="text-truncate">{r.nome}</span>
                  <span className="nt-hbar-track">
                    <i style={{ width: `${Math.round((r.valor / maxRevenue) * 100)}%` }} />
                  </span>
                  <span className="text-end nt-tabular" style={{ color: "var(--sp-ink-2)" }}>
                    {formatBRL(r.valor)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="d-flex flex-column gap-3 min-w-0">
          <section className="nt-card">
            <div className="nt-panel-head">
              <h2>Próximos vencimentos</h2>
            </div>
            <div className="nt-panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {upcoming.length === 0 && (
                <div className="text-secondary py-3" style={{ fontSize: 13 }}>
                  Nenhum título em aberto.
                </div>
              )}
              {upcoming.map((r) => {
                const due = new Date(r.dueDate);
                return (
                  <div key={r.id} className="nt-list-row">
                    <span className="nt-day">
                      <strong>{String(due.getUTCDate()).padStart(2, "0")}</strong>
                      {MONTHS[due.getUTCMonth()]}
                    </span>
                    <div className="flex-grow-1 min-w-0">
                      <div className="text-truncate" style={{ fontWeight: 500 }}>
                        {r.customer.razaoSocial}
                      </div>
                      {r.status === "VENCIDO" ? (
                        <StatusTag status="VENCIDO" />
                      ) : (
                        <span className="text-secondary" style={{ fontSize: 12.5 }}>
                          {r.reference}
                        </span>
                      )}
                    </div>
                    <span className="nt-tabular text-end">{formatBRL(r.value)}</span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="nt-card">
            <div className="nt-panel-head">
              <h2>Atividade recente</h2>
            </div>
            <div className="nt-panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {activity.length === 0 && (
                <div className="text-secondary py-3" style={{ fontSize: 13 }}>
                  Nenhuma atividade recente.
                </div>
              )}
              {activity.map((a, i) => (
                <div key={i} className="nt-list-row" style={{ alignItems: "flex-start", fontSize: 13.5 }}>
                  <span className="nt-tabular text-secondary flex-shrink-0" style={{ fontSize: 12, width: 78, paddingTop: 1 }}>
                    {formatDate(a.quando)}
                  </span>
                  <span className="min-w-0">{a.texto}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
